import { useEffect, useState } from 'react';
import PageTitle from '../Components/PageTitle';
import AxiosApi from '../Helper/axios.api';
import { useSelector, useDispatch } from "react-redux";
import { Link } from 'react-router-dom';
import { logout } from "../Action/Auth.action";
import { confirmAlert } from "react-confirm-alert";
import EditAnimator from "./EditAnimator";
import { Select } from 'react-select-virtualized';
import moment from "moment";

/**
 * Recursively creates a nested unordered list (<ul>) from a given object or array.
 * - If an item is a valid URL, it is wrapped inside an <a> tag.
 * - If an item is a float, it is displayed with 1 decimal precision.
 * - If an item is a Date or a date string, it is formatted in the user's locale.
 *
 * @param {*} item - The data to be transformed.
 * @returns {HTMLElement} - The generated list (<ul>) or appropriate HTML element.
 */
const objToUl = (item, parent=null) => {
  if (typeof item === 'object' && item !== null) {
    // Check for Date instance
    if (item instanceof Date) {
      return item.toLocaleString();
    }

    // Objects and arrays
    if (Array.isArray(item)) {
      if (item.length === 0) {
        return <i>Empty list</i>;
      } else {
        return <ul>{item.map((subItem, index) => (
          <li key={index}><strong>{index}:</strong> {objToUl(subItem, index)}</li>
        ))}</ul>;
      }
    } else {
      const entries = Object.entries(item);
      if (entries.length == 0) {
        return <i>Empty object</i>;
      } else {
        return <ul>{entries.map(([key, value]) => (
          <li key={key}><strong>{key}:</strong> {objToUl(value, key)}</li>
        ))}</ul>;
      }
    }

  } else if (typeof item === 'string') {
    // Check if it's a valid URL
    if (isValidURL(item)) {
      return <a href={item} target="_blank" rel="noopener noreferrer">{item}</a>;
    }

    // Check if it's an ISO 8601 date string
    const parsedDate = Date.parse(item);
    if (!isNaN(parsedDate)) {
      return new Date(parsedDate).toLocaleString();
    }

    if (parent === 'id' || (typeof parent === 'string' && parent.endsWith('_id'))) {
      return <CopyEl content={item} />
    }

    return item;

  } else if (typeof item === 'number' && !isNaN(item) && item % 1 !== 0) {
    return item.toFixed(1);

  } else {
    return item;
  }
};


/**
* Checks whether a given string is a valid URL.
*
* @param {string} str - The string to check.
* @returns {boolean} - True if the string is a valid URL, otherwise false.
*/
function isValidURL(str) {
  try {
    const url = new URL(str);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch (_) {
    return false;
  }
}


const CopyEl = ({content}) => {
  return <code
    style={{userSelect:'none', cursor:'pointer'}}
    title='Click to copy'
    onClick={(e) => {
      navigator.clipboard.writeText(content);
      e.target.innerText = "copied to clipboard!";
      setTimeout(() => { e.target.innerText = content; }, 1000)
    }}
  >
    {content}
  </code>
};


const MultiSelect = ({ options, selected, selectedLabels, setSelected }) => {
  const toggleOption = (option) => {
    const newSelected = selectedLabels.includes(option.value)
      ? selected.filter(o => o.value !== option.value)
      : [...selected, option];
    setSelected(newSelected);
  };

  return (
    <div className="border p-2 rounded m-2" style={{display: 'inline'}}>
      <span>State:</span>
      {options.map(option => (
        <label
          key={option.value}
          className='p-2'
        >
          <input
            type="checkbox"
            checked={selectedLabels.includes(option.value)}
            onChange={() => toggleOption(option)}
          />
          <span> {option.label}</span>
        </label>
      ))}
    </div>
  );
};

const ActiveSessions = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const stateOptions = [
    { label: 'Starting', value: 'ST' },
    { label: 'Running', value: 'RU' },
    { label: 'Closing', value: 'CI' },
    { label: 'Closed', value: 'CD' },
    { label: 'Terminated', value: 'TE' },
  ];
  const [selectedStates, setSelectedStates] = useState([
    { label: 'Starting', value: 'ST' },
    { label: 'Running', value: 'RU' },
    { label: 'Closing', value: 'CI' },
    // { label: 'Closed', value: 'CD' },
    // { label: 'Terminated', value: 'TE' },
  ]);
  const selectedStateLabels = selectedStates.map(o => o.value);

  const [selectedEditAnimator, setSelectedEditAnimator] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);

  const orderByOptions = [
    { label: 'Priority (high first)', value: '-priority' },
    { label: 'Priority (low first)', value: 'priority' },
    { label: 'Last update (recent first)', value: '-last_update' },
    { label: 'Last update (oldest first)', value: 'last_update' },
    { label: 'Name (A-Z)', value: 'name' },
    { label: 'Name (Z-A)', value: '-name' },
  ]
  const [orderBy, setOrderBy] = useState(orderByOptions[0].value);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const [workers, setWorkers] = useState([]);
  const [workerStatuses, setWorkerStatuses] = useState({});

  const endSession = (id) => {
    confirmAlert({
      title: "End Session",
      message: "Are you sure you want to end this session?",
      buttons: [
        { label: "Yes", onClick: async () => {
          const data = {
            user_id: auth.userDeatils.user_id,
            session_id: id,
            method: 'get',
            endpoint: '/close-session/'+id,
          };
          const response = await AxiosApi.withToken(auth.accessToken, "webrtc", "post", data);
          console.log(response);
          await fetchData();
        }},
        { label: "No", onClick: async () => {} },
      ],
    });
  };

  const fetchData = async () => {
    setError(null);
    try {
      const data = {
        user_id: auth.userDeatils.user_id,
        state: selectedStateLabels,
        order_by: orderBy,
      };
      const response = await AxiosApi.withToken(auth.accessToken, "admin/worker", "post", data);
      if (response.login === false) {
        dispatch(logout());
        navigate("/login");
      } else if (response.status) {
        setLastUpdate(new Date());
        setWorkers(response.data.workers);
        console.log(response.data.workers);
      } else {
        console.error(response);
        throw Error(response.message);
      }
    } catch (error) {
      setError(error.message);
      console.log(error);
    }
  };
  useEffect(() => {
    fetchData();
  }, [selectedStates, orderBy, isUpdate]);
  useEffect(() => {
    workers.forEach((worker) => {
      if (worker.is_live)
        fetchWorkerStatus(worker)
    });
  }, [workers]);

  const fetchWorkerStatus = async (worker) => {
    try {
      setWorkerStatuses((workerStatuses) => ({
        ...workerStatuses,
        [worker.id]: {'status':'Fetching ...'}
      }));
      const data = {
        user_id: auth.userDeatils.user_id,
        worker_id: worker.id,
        method: 'get',
        endpoint: '/status',
      };
      const response = await AxiosApi.withToken(auth.accessToken, "webrtc", "post", data);
      if (response.status !== "success") throw Error(response.message);

      setWorkerStatuses((workerStatuses) => ({
        ...workerStatuses,
        [worker.id]: response.data,
      }));

    } catch(error) {
      setWorkerStatuses((workerStatuses) => ({
        ...workerStatuses,
        [worker.id]: {'error':error.message || 'Failed to fetch status.'}
      }));
    }
  }

  return (
    <>
      <PageTitle title="DeepTherapy | Active Sessions" />
      <div className="virtualization">
        <div className="card w-100">
          <div className="card-body">
            <div>
              <button className='btn btn-primary' onClick={fetchData}>Update all</button>
              <MultiSelect options={stateOptions} selected={selectedStates} setSelected={setSelectedStates} selectedLabels={selectedStateLabels} />
              <div style={{display:'inline-block', width: '16rem'}}><Select
                defaultValue={orderByOptions[0]}
                options={orderByOptions}
                onChange={(option) => setOrderBy(option.value)}
                style={{
                  display:'inline'
                }}
              /></div>
            </div>
            {error && <p style={{fontWeight:'bold', color:'red'}}>{error}</p>}
            <p>Last update: <i>{new Date(Date.parse(lastUpdate)).toLocaleString()}</i></p>
            <h2>Workers</h2>
            <div>
              { workers.length > 0 ? (
                workers.map((worker) => (
                  <div key={worker.id} className='border p-2 my-4'>
                    <p>
                      <span style={{backgroundColor: worker.is_live ? 'green' : 'red', borderRadius: '100%', width: '1rem', height: '1rem', display: 'inline-block'}} />
                      <span className='mx-2'>Name: <b>{worker.name}</b></span>
                      <span className='mx-2'>State: <b style={(!worker.is_live && worker.state.label == 'Running') ? {textDecoration: 'line-through'} : {}}>{worker.state.label}</b></span>
                      <span className='mx-2'>Priority: <b>{worker.priority}</b></span>
                      <br />
                      <span className='mx-2'>ID: <CopyEl content={worker.id} /></span>
                      <span className='mx-2'>Provider: <code>{worker.provider}</code></span>
                      <span className='mx-2'>URL: <CopyEl content={worker.base_url} /></span>
                      <span className='mx-2'>Audio animator? <code>{worker.audio_animator ? 'Yes' : 'No'}</code></span>
                      <span className='mx-2'>GPUs: <code>{worker.gpu_count}</code></span>
                      <span className='mx-2'>Last update: <code>{new Date(Date.parse(worker.last_update)).toLocaleString()}</code></span>
                    </p>
                    <div>
                      <p>
                        <button
                          className='btn btn-primary mx-1'
                          onClick={() => setSelectedEditAnimator(worker)}
                        >
                          <i className={"fa text-white fa-edit"}></i> Edit
                        </button>
                        <button
                          className='btn btn-primary mx-1'
                          onClick={() => fetchWorkerStatus(worker)}
                        >
                          <i className={"fa text-white fa-spinner"}></i> Fetch Status
                        </button>
                      </p>
                      <div>
                        <h3>Status</h3>
                        {workerStatuses[worker.id] && objToUl(workerStatuses[worker.id])}
                      </div>
                    </div>
                    <div>
                      <h3>Sessions</h3>
                      {worker.sessions.length > 0 ? (
                        worker.sessions.map((session) => (
                          <div key={session.id} className='border my-2'>
                            <p className='p-2 m-0'>
                              <span className='mx-2'>State: <b>{session.state}</b></span>
                              <span className='mx-2'>Start: <b>{new Date(Date.parse(session.time_start)).toLocaleString()}</b></span>
                              <span className='mx-2'>End: <b>{new Date(Date.parse(session.time_end)).toLocaleString()}</b></span>
                              <span className='mx-2'>ID: <CopyEl content={session.id} /></span>
                              <span className='mx-2'>Reservation ID: <CopyEl content={session.reservation_id} /></span>
                              <span className='mx-2'>Is Active: <code>{session.is_active ? "Yes" : "No"}</code></span>
                              <br />
                              <Link to={'/reservation/'+session.reservation_id} className='mx-2'>Go to Reservation</Link>
                              <button className='btn btn-primary mx-2' onClick={(e) => {
                                const sibling = e.target.parentElement.parentElement.lastChild;
                                sibling.style.display = sibling.style.display === 'none' ? 'block' : 'none';
                              }}>Toggle details</button>
                              <button className='btn btn-primary mx-2'  onClick={(e) => endSession(session.id)}>Kill session</button>
                            </p>
                            <div style={{display:'none'}}>{objToUl(session)}</div>
                          </div>
                        ))
                      ) : (<p><i>No sessions are set for this worker</i></p>)}
                    </div>
                  </div>
                ))
              ) : <b>No workers found.</b>}
            </div>
          </div>
        </div>
      </div>
      <EditAnimator
        isUpdate={isUpdate}
        setIsUpdate={setIsUpdate}
        animator={selectedEditAnimator}
        setAnimator={setSelectedEditAnimator}
        stateOptions={stateOptions}
      />
    </>
  );
}

export default ActiveSessions;
